// src/pages/reports/IrAnalysis/ReportsIrTable.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  ListItemText,
  ListSubheader,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Select,
  Checkbox,
  Tooltip,
} from "@mui/material";
import PaginationComponent from "../../brands/PaginationReports";
import { exportIrAnalysis, exportIrSummReport_csv } from "../api";
import Export from "../../../../src/assests/images/export.svg";
import Sorting from "../../../../src/assests/images/Sorting.svg";
import { getToken } from "../../../utils/utils";
import PaginationReports from "../../brands/PaginationReports";

/** RewardsTable-style rotating arrow */
const CustomArrow: React.FC<{ open: boolean; onClick: () => void }> = ({ open, onClick }) => (
  <span
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    style={{
      display: "inline-block",
      width: 11,
      right: "10px",
      height: "8px",
      borderRight: "2px solid #000",
      borderBottom: "2px solid #000",
      transform: open ? "rotate(-135deg)" : "rotate(45deg)",
      transition: "transform 0.2s ease",
      marginLeft: "20px",
      position: "relative",
      cursor: "pointer",
    }}
  />
);

type Order = "asc" | "desc";

interface ReportsIrTableProps {
  searchQuery: string;
  dateRange: [Date | null, Date | null];
  appliedFilters?: {
    city?: string | string[];
    locations?: string[] | string;
    outlet_category?: string[];
    pmi_entity_code?: string[];
    outlet_name?: string[];
  } | null;
  onBackendFilterOptions?: (opts: {
    available_cities: string[];
    available_outlet_names: string[];
    available_pmi_codes: string[];
    available_outlet_categories: string[];
  }) => void;
}

/** Strict IR Report columns (label + backend key) */
const RAW_COLUMNS = [
  { label: "Region", value: "Region" },
  { label: "ASM Name", value: "ASM Name" },
  { label: "Area Code", value: "Area Code" },
  { label: "TSE Name", value: "TSE Name" },
  { label: "Territory Code", value: "Territory Code" },
  { label: "PMI Entity Code", value: "PMI Entity Code" },
  { label: "Outlet Name", value: "Outlet Name" },
  { label: "City", value: "City" },
  { label: "Outlet Category", value: "Outlet Category" },
  { label: "Latest Image Capture Date", value: "Latest Image Capture Date" },
  { label: "Latest IR Self Facings", value: "Latest IR Self Facings" },
  { label: "Latest IR Competition Facings", value: "Latest IR Competition Facings" },
  { label: "Latest IR Others Facings", value: "Latest IR Others Facings" },
  { label: "Latest Image Link", value: "Latest Image Link" },
] as const;

type ColumnLabel = (typeof RAW_COLUMNS)[number]["label"];
type ColumnKey = (typeof RAW_COLUMNS)[number]["value"];

const ALL_LABELS: ColumnLabel[] = RAW_COLUMNS.map((c) => c.label);
const labelToKey = RAW_COLUMNS.reduce<Record<ColumnLabel, ColumnKey>>((acc, c) => {
  acc[c.label] = c.value;
  return acc;
}, {} as any);

/** Choose sensible defaults (like first ~6 similar to ReportsLogTable) */
const DEFAULT_VISIBLE: ColumnLabel[] = [
  "Region",
  "ASM Name",
  "TSE Name",
  "PMI Entity Code",
  "Outlet Name",
  "City",
];

const DEFAULT_SET = new Set<ColumnLabel>(DEFAULT_VISIBLE);
const withDefaultsIfEmpty = (arr: ColumnLabel[]) =>
  arr && arr.length ? arr : [...DEFAULT_VISIBLE];


const ReportsIrTable: React.FC<ReportsIrTableProps> = ({
  searchQuery,
  dateRange: _ignored, // UI-only
  appliedFilters = null,
  onBackendFilterOptions,
}) => {
  const [rows, setRows] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(totalRows / itemsPerPage);

  // Column picker state (mirrors ReportsLogTable)
  const [visibleColumns, setVisibleColumns] = useState<ColumnLabel[]>(DEFAULT_VISIBLE);
  const [tempSelectedColumns, setTempSelectedColumns] =
    useState<ColumnLabel[]>(DEFAULT_VISIBLE);
  const [colSelectOpen, setColSelectOpen] = useState(false);
  const [colSearch, setColSearch] = useState("");
  const colSearchRef = useRef<HTMLInputElement | null>(null);

  // Sorting (use backend keys)
  const [orderBy, setOrderBy] = useState<ColumnKey>("Outlet Name");
  const [order, setOrder] = useState<Order>("asc");

  // Reset page on filter/search change (same behavior)
  useEffect(() => {
    setPage(1);
  }, [appliedFilters, searchQuery]);

  // Column search list
  const filteredColumns = useMemo(() => {
    const q = colSearch.trim().toLowerCase();
    if (!q) return ALL_LABELS;
    return ALL_LABELS.filter((name) => name.toLowerCase().includes(q));
  }, [colSearch]);

  const allFilteredSelected =
    filteredColumns.length > 0 &&
    filteredColumns.every((c) => tempSelectedColumns.includes(c));

  const toggleSelectAllOnFiltered = () => {
  if (allFilteredSelected) {
    // Deselect only non-default columns from the filtered set
    setTempSelectedColumns((prev) => {
      const next = prev.filter(
        (p) => !filteredColumns.includes(p) || DEFAULT_SET.has(p)
      );
      // Safety: ensure defaults are always present
      DEFAULT_VISIBLE.forEach((d) => {
        if (!next.includes(d)) next.push(d);
      });
      return next;
    });
  } else {
    // Select all filtered + ensure defaults remain selected
    setTempSelectedColumns((prev) =>
      Array.from(new Set<ColumnLabel>([...prev, ...filteredColumns, ...DEFAULT_VISIBLE]))
    );
  }
};


  // STRICT payload (unchanged)
  const buildPayload = () => {
    const payload: Record<string, any> = {
      page,
      limit: itemsPerPage,
    };
    if (appliedFilters?.city) payload.city = appliedFilters.city;

    if (
      appliedFilters?.locations &&
      (Array.isArray(appliedFilters.locations)
        ? appliedFilters.locations.length
        : String(appliedFilters.locations).length)
    ) {
      payload.locations = appliedFilters.locations;
    }
    if (appliedFilters?.outlet_category?.length)
      payload.outlet_category = appliedFilters.outlet_category;
    if (appliedFilters?.pmi_entity_code?.length)
      payload.pmi_entity_code = appliedFilters.pmi_entity_code;
    if (appliedFilters?.outlet_name?.length)
      payload.outlet_name = appliedFilters.outlet_name;

    if (searchQuery && searchQuery.trim()) {
      payload.search = searchQuery.trim();
    }
    return payload;
  };

  // Fetch table data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = getToken();
        const payload = buildPayload();
        const response = await exportIrAnalysis(payload, token!);

        const resultsArray = response?.results ?? [];
        const totalRowsValue = response?.total_rows ?? 0;

        setRows(resultsArray);
        setTotalRows(totalRowsValue);

        onBackendFilterOptions?.({
          available_cities: Array.isArray(response?.available_cities) ? response.available_cities : [],
          available_outlet_names: Array.isArray(response?.available_outlet_names) ? response.available_outlet_names : [],
          available_pmi_codes: Array.isArray(response?.available_pmi_codes) ? response.available_pmi_codes : [],
          available_outlet_categories: Array.isArray(response?.available_outlet_categories) ? response.available_outlet_categories : [],
        });
      } catch (err) {
        console.error("Failed to fetch IR report:", err);
        setRows([]);
        setTotalRows(0);
        onBackendFilterOptions?.({
          available_cities: [],
          available_outlet_names: [],
          available_pmi_codes: [],
          available_outlet_categories: [],
        });
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, appliedFilters, searchQuery]);

  const handleSort = (label: ColumnLabel) => {
    const key = labelToKey[label];
    const isAsc = orderBy === key && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(key);
  };

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const aVal = a?.[orderBy] ?? "";
      const bVal = b?.[orderBy] ?? "";
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return order === "asc" ? -1 : 1;
      if (aStr > bStr) return order === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [rows, orderBy, order]);

  // CSV Export (visible columns -> backend keys)
  const handleExport = async () => {
    try {
      const token = getToken();
      const selectedKeys: ColumnKey[] = visibleColumns.map((l) => labelToKey[l]);

      const payload = {
        ...buildPayload(),
        columns: selectedKeys, // include visible columns
        limit: "all",
        export: true,
      };

      const blob = await exportIrSummReport_csv(payload, token, true);

      if (blob instanceof Blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `ir_summary_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        console.error("Unexpected response from exportIrSummReport_csv:", blob);
      }
    } catch (err) {
      console.error("IR Summary Export failed:", err);
    }
  };

  const renderCell = (key: ColumnKey, raw: any) => {
    if (key === "Latest Image Capture Date" && raw && raw !== "-") {
      const d = new Date(raw);
      return isNaN(d.getTime()) ? String(raw) : d.toLocaleDateString();
    }
    return raw ? String(raw) : "-";
  };

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "10px",
        border: "1px solid #cfcfcf",
        margin: "30px 32px",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          padding: "25px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
          Total {totalRows} Records
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/* Column picker (same UX as ReportsLogTable) */}
          <Box sx={{ marginRight: "20px" }}>
            <Tooltip
              title={tempSelectedColumns.length ? tempSelectedColumns.join(", ") : "Select Columns"}
              placement="top"
              arrow
              PopperProps={{ modifiers: [{ name: "offset", options: { offset: [0, 10] } }] }}
            >
              <FormControl sx={{ minWidth: 240, width: 240 }}>
                <Select
                  multiple
                  displayEmpty
                  value={tempSelectedColumns}
                  open={colSelectOpen}
                  onOpen={() => setColSelectOpen(true)}
                  onClose={() => {
                    setColSelectOpen(false);
                    const next = withDefaultsIfEmpty(tempSelectedColumns);
                    setTempSelectedColumns(next);   // keep internal state in sync
                    setVisibleColumns(next);        // render with non-empty set
                    setColSearch("");
                  }}

                  onChange={(e) => {
                    const val = e.target.value;
                    const arr =
                    typeof val === "string" ? (val as string).split(",") : (val as ColumnLabel[]);

                    if (Array.isArray(arr) && (arr as any[]).includes("__ALL__")) {
    // handled by MenuItem onClick but keep safety
                      toggleSelectAllOnFiltered();
                      return;
                    }

  // allow free unselect; if empty, restore defaults
                const next = withDefaultsIfEmpty(Array.isArray(arr) ? (arr as ColumnLabel[]) : []);
                setTempSelectedColumns(next);
            }}

                  renderValue={(selected) => {
                    if ((selected as string[]).length === 0) return "Select Columns";
                    return (selected as string[]).join(", ");
                  }}
                  IconComponent={() => (
                    <CustomArrow open={colSelectOpen} onClick={() => setColSelectOpen((v) => !v)} />
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 240,
                        paddingTop: 0,
                        paddingBottom: 0,
                        overflow: "overlay",
                        borderRadius: 10,
                        background: "#fff",
                        boxShadow: "none",
                      },
                    },
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    disableAutoFocusItem: true,
                    MenuListProps: {
                      style: {
                        paddingTop: 0,
                        paddingBottom: 0,
                        maxHeight: "250px",
                        overflowY: "auto",
                      },
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.stopPropagation();
                          (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();
                          setTimeout(() => colSearchRef.current?.focus(), 0);
                        }
                      },
                    },
                  }}
                  sx={{
                    minWidth: 240,
                    height: "40px",
                    borderRadius: "10px",
                    backgroundColor: "#fff",
                    marginRight: "8px",
                    width: "240px",
                    position: "relative",
                    "& .MuiSelect-icon": { display: "none" }, // hide default icon
                    "&:hover, &.Mui-focused, &.Mui-focusVisible": {
                      background: "transparent !important",
                      boxShadow: "none !important",
                      outline: "none !important",
                    },
                  }}
                >
                  {/* sticky search bar */}
                  <ListSubheader
                    sx={{
                      bgcolor: "white",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        px: 1,
                        py: 0.5,
                        border: "1px solid #ccc",
                        borderRadius: 1,
                        bgcolor: "transparent",
                      }}
                      onClick={(ev) => ev.stopPropagation()}
                    >
                      <input
                        ref={colSearchRef}
                        value={colSearch}
                        onChange={(e) => setColSearch(e.target.value)}
                        placeholder="Search columns..."
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          fontSize: 14,
                          background: "transparent",
                          padding: "4px",
                          height: "40px",
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();
                        }}
                      />
                      {colSearch && (
                        <Box
                          component="span"
                          sx={{ cursor: "pointer", fontWeight: "bold", fontSize: 18, lineHeight: 1, px: 1 }}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setColSearch("");
                            setTimeout(() => colSearchRef.current?.focus(), 0);
                          }}
                        >
                          ×
                        </Box>
                      )}
                    </Box>
                  </ListSubheader>

                  {/* Select all (filtered) */}
                  <MenuItem
                    dense
                    value="__ALL__"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      toggleSelectAllOnFiltered();
                    }}
                  >
                    <Checkbox checked={allFilteredSelected} />
                    <ListItemText primary="Select All" />
                  </MenuItem>

                  {filteredColumns.length ? (
                    filteredColumns.map((name) => (
                      <MenuItem key={name} value={name}>
                        <Checkbox checked={tempSelectedColumns.includes(name)} />
                        <ListItemText primary={name} />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled dense>
                      <ListItemText primary="No results" />
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Tooltip>
          </Box>

          <Button
            variant="contained"
            sx={{
              textTransform: "capitalize",
              backgroundColor: "#fff",
              fontSize: "14px",
              fontWeight: 500,
              height: "40px",
              padding: "16px",
              borderRadius: "10px",
              color: "#C8172F",
              border: "1px solid #C8172F",
              boxShadow: "none",
            }}
            startIcon={<img src={Export} alt="export" />}
            onClick={handleExport}
            disabled={totalRows === 0}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ boxShadow: "none", maxWidth: "100%", overflowX: "auto" }}>
        <Table sx={{ minWidth: visibleColumns.length * 150 }}>
          <TableHead sx={{ background: "#EEF4FF" }}>
            <TableRow>
              {visibleColumns.map((label) => {
                const key = labelToKey[label];
                return (
                  <TableCell
                    key={label}
                    sx={{
                      minWidth: 150,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 300,
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === key}
                      direction={orderBy === key ? order : "asc"}
                      onClick={() => handleSort(label)}
                      IconComponent={() => <img src={Sorting} alt="sort" style={{ width: 16, height: 16 }} />}
                    >
                      {label}
                    </TableSortLabel>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedRows.length > 0 ? (
              sortedRows.map((row, idx) => (
                <TableRow key={idx}>
                  {visibleColumns.map((label) => {
                    const key = labelToKey[label] as ColumnKey;
                    const raw = row?.[key];
                    const display = renderCell(key, raw);
                    return (
                      <TableCell
                        key={key}
                        sx={{
                          maxWidth: 260,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={typeof display === "string" ? (display as string) : undefined}
                      >
                        {display}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} align="center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <PaginationReports page={page} limit={itemsPerPage} totalPages={totalPages} setPage={setPage} />
    </Box>
  );
};

export default ReportsIrTable;
